// ChatPanel — Text chat UI for Anna teaching sessions
//
// Renders a scrollable message list with markdown support, streaming
// indicator, error banner, and auto-growing text input.
// Voice input is supported via optional voiceAvailability/voiceState props.
// When voice is active the textarea is disabled and shows a "Listening..." placeholder.

import { useState, useRef, useEffect, type KeyboardEvent } from 'react';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import type { ChatMessage } from '@hooks/useAnnaChat';
import type { VoiceAvailabilityState } from '@hooks/useVoiceAvailability';
import type { VoiceInputState } from '@hooks/useVoiceInput';
import VoiceMicButton from './VoiceMicButton';

interface ChatPanelProps {
  messages: ChatMessage[];
  isStreaming: boolean;
  error: string | null;
  onSendMessage: (text: string) => void;
  onClearError?: () => void;
  className?: string;
  accentColor?: string;
  /** Voice input availability state */
  voiceAvailability?: VoiceAvailabilityState;
  /** Voice input state */
  voiceState?: VoiceInputState;
  /** Voice input error message */
  voiceError?: string | null;
  /** Current audio input level 0-1 */
  audioLevel?: number;
  /** Interim transcript shown in real-time while user speaks */
  interimTranscript?: string;
  /** Toggle voice recording on/off */
  onVoiceToggle?: () => void;
  /** Whether Anna's voice output (TTS) is enabled */
  voiceOutputEnabled?: boolean;
  /** Whether Anna is currently speaking */
  isAnnaSpeaking?: boolean;
  /** Toggle Anna's voice output on/off */
  onVoiceOutputToggle?: () => void;
  /**
   * Pre-fill the chat input with this text (e.g. when user clicks a concept map node).
   * The component sets this as the input value and focuses the textarea, then the
   * parent must reset it to '' via onPrefillConsumed to avoid re-triggering.
   */
  prefillText?: string;
  onPrefillConsumed?: () => void;
}

const TOOL_ICONS: Record<string, string> = {
  show_content_block: '\u{1F4C4}',
  show_concept_map: '\u{1F5FA}',
  show_exercise: '\u{270F}',
  update_bloom_level: '\u{1F4C8}',
};

export default function ChatPanel({
  messages,
  isStreaming,
  error,
  onSendMessage,
  onClearError,
  className = '',
  accentColor = 'blue',
  voiceAvailability,
  voiceState,
  voiceError,
  audioLevel,
  interimTranscript,
  onVoiceToggle,
  voiceOutputEnabled,
  isAnnaSpeaking,
  onVoiceOutputToggle,
  prefillText,
  onPrefillConsumed,
}: ChatPanelProps) {
  const { t } = useTranslation('session');
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when new messages arrive or content streams in
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    }
  }, [input]);

  // Pre-fill input when a concept map node is clicked (dialog mode)
  useEffect(() => {
    if (prefillText) {
      setInput(prefillText);
      textareaRef.current?.focus();
      onPrefillConsumed?.();
    }
  }, [prefillText]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;
    onSendMessage(trimmed);
    setInput('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (date: Date) =>
    date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <div className={`flex flex-col h-full bg-gray-950 ${className}`}>
      {/* Message list */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                msg.role === 'user'
                  ? 'bg-gray-800 text-gray-100'
                  : `bg-${accentColor}-950/40 text-gray-100 border border-${accentColor}-900/30`
              }`}
            >
              {msg.role === 'assistant' ? (
                <div className="prose prose-invert prose-sm max-w-none">
                  <ReactMarkdown>{msg.content || '\u200B'}</ReactMarkdown>
                </div>
              ) : (
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              )}

              {/* Tool call badges */}
              {msg.toolCalls && msg.toolCalls.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-white/10">
                  {msg.toolCalls.map((tc) => (
                    <span
                      key={tc.id}
                      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-${accentColor}-900/30 text-${accentColor}-300`}
                    >
                      {TOOL_ICONS[tc.name] || '\u{1F527}'}{' '}
                      {t(`chat.toolCallLabels.${tc.name}`, tc.name)}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <span className="text-[10px] text-gray-600 mt-1 px-2">
              {formatTime(msg.timestamp)}
            </span>
          </div>
        ))}

        {/* Streaming indicator */}
        {isStreaming && (
          <div className="flex items-center gap-2 text-gray-500 text-xs pl-2">
            <span className="flex gap-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-500 animate-bounce [animation-delay:0ms]" />
              <span className="w-1.5 h-1.5 rounded-full bg-gray-500 animate-bounce [animation-delay:150ms]" />
              <span className="w-1.5 h-1.5 rounded-full bg-gray-500 animate-bounce [animation-delay:300ms]" />
            </span>
            {t('chat.streaming')}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Error banner */}
      {error && (
        <div className="mx-4 mb-2 px-3 py-2 rounded-lg bg-red-950/50 border border-red-900/50 text-red-300 text-sm flex items-center justify-between">
          <span>{error}</span>
          {onClearError && (
            <button
              onClick={onClearError}
              className="ml-2 text-red-400 hover:text-red-200 text-lg leading-none"
              aria-label="Dismiss error"
            >
              &times;
            </button>
          )}
        </div>
      )}

      {/* Input area */}
      <div className="border-t border-gray-800 px-4 py-3">
        {/* Interim transcript preview — shown in real-time while user speaks */}
        {interimTranscript && (
          <div className="mb-2 px-3 py-1.5 bg-green-500/10 rounded-lg text-sm text-green-400 italic">
            {interimTranscript}
          </div>
        )}

        <div className="flex items-end gap-2">
          {/* Mic button — only rendered when voice input is configured */}
          {voiceAvailability && onVoiceToggle && (
            <VoiceMicButton
              availability={voiceAvailability}
              voiceState={voiceState ?? 'idle'}
              audioLevel={audioLevel ?? 0}
              isStreaming={isStreaming}
              onToggle={onVoiceToggle}
              voiceError={voiceError}
            />
          )}

          {/* TTS toggle — Anna's voice on/off */}
          {onVoiceOutputToggle && (
            <button
              onClick={onVoiceOutputToggle}
              className={`
                shrink-0 w-10 h-10 rounded-full flex items-center justify-center
                transition-all duration-200
                ${voiceOutputEnabled
                  ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/20'
                  : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                }
                ${isAnnaSpeaking ? 'animate-pulse' : ''}
              `}
              aria-label={t(voiceOutputEnabled ? 'voice.tts.disable' : 'voice.tts.enable')}
              title={t(voiceOutputEnabled ? 'voice.tts.disable' : 'voice.tts.enable')}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {voiceOutputEnabled ? (
                  <>
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                  </>
                ) : (
                  <>
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                  </>
                )}
              </svg>
            </button>
          )}

          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            // Show "Listening..." placeholder when voice is active so the user
            // understands their speech is being captured (not typed input).
            placeholder={
              voiceState === 'listening'
                ? t('chat.placeholderListening', 'Listening...')
                : t('chat.placeholder')
            }
            rows={1}
            className="flex-1 resize-none rounded-xl bg-gray-900 border border-gray-700 px-3 py-2.5 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:border-gray-500 transition-colors"
            // Disable typing while voice is capturing to avoid mixed input
            disabled={isStreaming || voiceState === 'listening'}
          />
          <button
            onClick={handleSend}
            disabled={isStreaming || !input.trim()}
            className={`shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              isStreaming || !input.trim()
                ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                : `bg-${accentColor}-600 hover:bg-${accentColor}-500 text-white`
            }`}
          >
            {t('chat.send')}
          </button>
        </div>
      </div>
    </div>
  );
}
